/**
 * 루미나 화면 층 신작 — 시안 40-overlays 상담 채팅(cw-). 동작은 블랙·디자인1과 같은 hooks/useChatWidget.js, 첨부 표시는 ChatAttachment.jsx.
 * WHY: SPEC §2 — 가용성 판정·대화 조회·전송·첨부 업로드·읽음·소켓 병합·15초 폴백은 공용, JSX·CSS 만 루미나 체계로.
 *   · 우하단 FAB(.cw-fab + 미확인 배지) + 패널(.cw-panel: 머리 아이콘·"상담 채팅"·상대 이름, 말풍선, 첨부 대기 필, 첨부·입력·전송). 이모지 대신 인라인 SVG.
 *   · 위치(표현 전용 useEffect): 모바일 하단 내비(#bnav, 1100px 이하 표시)와 스포츠 모바일 슬립 바(고정 .slip) 위로 올라가고, 슬립이 펼쳐지면 숨긴다 — 시안 cwPlace 규칙.
 *
 * [2026-08-07] 실시간 상담 채팅 위젯 (유저페이지 우하단 — 정본: docs/채팅기능_구현_정본.md)
 */
import React, { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import useChatWidget from '../../hooks/useChatWidget';
import ChatAttachment from './ChatAttachment';
import './ChatWidget.css';

// ---------- 화면 층 전용 아이콘(색·두께는 CSS .cw-* svg 가 정한다) ----------
const IcoChat = () => (<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M21 12a8 8 0 0 1-8 8H7l-4 3V12a8 8 0 0 1 8-8h2a8 8 0 0 1 8 8z" /><path d="M8 12h.01M12 12h.01M16 12h.01" /></svg>);
const IcoHeadset = () => (<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M4 14v-3a8 8 0 0 1 16 0v3" /><rect x="3" y="13" width="4" height="6" rx="2" /><rect x="17" y="13" width="4" height="6" rx="2" /><path d="M19 19a3 3 0 0 1-3 3h-3" /></svg>);
const IcoX = () => (<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M6 6l12 12M18 6L6 18" /></svg>);
const IcoClip = () => (<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M21 11.5l-8.5 8.5a5 5 0 0 1-7-7l9-9a3.5 3.5 0 0 1 5 5l-9 9a2 2 0 0 1-3-3l8-8" /></svg>);
// 스포츠 페이지 모바일 하단 고정 슬립 바(루미나 .slip — 1100px 이하에서 position:fixed, 접힌 상태 .collapsed)
const SLIP_SELECTOR = '.slip';

const ChatWidget = () => {
  const chat = useChatWidget();
  const { available } = chat;
  const location = useLocation();
  const rootRef = useRef(null);

  // ── 위치(표현 전용): 하단 내비(#bnav)·스포츠 모바일 슬립 바 위로. 화면 전환·창 크기·슬립 접힘/펼침에 따라 다시 잰다 ──
  //   #bnav 는 fixed 라 offsetParent 가 null 이므로 표시 여부는 getComputedStyle(display) 로 판정(시안 cwPlace 와 동일).
  useEffect(() => {
    if (!available) return undefined;
    const root = rootRef.current;
    if (!root) return undefined;
    let raf = 0;
    const bnavHeight = () => {
      const n = document.getElementById('bnav');
      return (n && window.getComputedStyle(n).display !== 'none') ? n.offsetHeight : 0;
    };
    const place = () => {
      // 슬립은 1100px 이하에서만 하단 고정 바가 된다(시안 규칙). 스포츠 페이지는 카트 배치에 따라 .slip 이 둘(우측 패널형·하단 바형) 중 하나만 렌더되므로
      // 실제로 보이고(fixed·높이>0) 있는 것을 고른다.
      const narrow = window.innerWidth <= 1100;
      const slip = narrow
        ? Array.from(document.querySelectorAll(SLIP_SELECTOR)).find((el) => el.offsetHeight > 0 && window.getComputedStyle(el).position === 'fixed')
        : null;
      if (slip && !slip.classList.contains('collapsed')) {
        // 슬립이 펼쳐진 동안(최대 80vh)은 위젯을 숨긴다
        root.hidden = true;
        document.documentElement.style.setProperty('--cw-clear', '0px');
        return;
      }
      root.hidden = false;
      const extra = bnavHeight();
      if (slip) root.style.bottom = `${slip.offsetHeight + 16 + extra}px`;
      else root.style.bottom = extra ? `${20 + extra}px` : '';
      /* [2026-10-31] 버튼이 화면 아래에서 차지하는 높이(화면 아래 끝 → 버튼 위 끝)를 알린다 — styles/lumina.css 바닥글 아래 여백이
         이만큼 비워, 맨 아래까지 내려도 바닥글 마지막 줄이 이 버튼에 가리지 않게 한다(360px 폭에서 저작권 줄이 버튼 밑에 깔리던 결함).
         대화창이 열려 있어도 버튼(.cw-fab)만 잰다 — 대화창 높이까지 넣으면 열 때마다 페이지 길이가 출렁인다. */
      const fab = root.querySelector('.cw-fab');
      if (fab) document.documentElement.style.setProperty('--cw-clear', `${Math.max(0, Math.round(window.innerHeight - fab.getBoundingClientRect().top))}px`);
    };
    const schedule = () => { window.cancelAnimationFrame(raf); raf = window.requestAnimationFrame(place); };
    schedule();
    window.addEventListener('resize', schedule);
    // 화면 안 요소(슬립 바·내비)의 등장·접힘은 main 아래 DOM 변화로 감지
    const target = document.querySelector('main') || document.body;
    const mo = new MutationObserver(schedule);
    mo.observe(target, { childList: true, subtree: true, attributes: true, attributeFilter: ['class'] });
    return () => {
      window.cancelAnimationFrame(raf);
      window.removeEventListener('resize', schedule);
      mo.disconnect();
      document.documentElement.style.removeProperty('--cw-clear');   // 버튼이 사라지면 바닥글은 종전 여백으로
    };
  }, [available, location.pathname]);

  if (!available) return null;

  return (
    <div className="cw-root" ref={rootRef}>
      {chat.open && (
        <div className="cw-panel" role="dialog" aria-label="상담 채팅">
          <div className="cw-h">
            <span className="cw-av"><IcoHeadset /></span>
            <div className="cw-tt">
              <span className="cw-eyebrow">상담 채팅</span>
              <span className="cw-name">{chat.agentNickname}</span>
            </div>
            <button type="button" className="cw-x" onClick={chat.closePanel} aria-label="닫기"><IcoX /></button>
          </div>
          <div className="cw-body" ref={chat.bodyRef} onScroll={chat.onBodyScroll}>
            {chat.closedNotice && (
              <div className="cw-notice">상담이 종료되었습니다. 새 문의는 메시지를 보내면 시작됩니다.</div>
            )}
            {chat.messages.length === 0 && !chat.closedNotice && (
              <div className="cw-notice">무엇을 도와드릴까요? 메시지를 남겨 주세요.</div>
            )}
            {chat.messages.map(m => {
              const mine = m.sender_type === 'user';
              return (
                <div key={m.client_uuid || m.id} className={`cw-row${mine ? ' cw-mine' : ''}`}>
                  {!mine && <div className="cw-sender">{chat.agentNickname}</div>}
                  <div className="cw-bubble">
                    {m.content}
                    <ChatAttachment
                      message={m}
                      mediaClassName="cw-media"
                      noteClassName="cw-attach-note"
                      onLoaded={chat.onMediaLoaded}
                    />
                  </div>
                  {mine && m.read_at && <div className="cw-read">읽음</div>}
                </div>
              );
            })}
          </div>
          {chat.hasNewBelow && (
            <button type="button" className="cw-new-below" onClick={chat.scrollToLatest}>새 메시지 보기</button>
          )}
          <div className="cw-inbar">
            {chat.pendingFile && (
              <div className="cw-pending">
                <span>{chat.pendingFile.name}</span>
                <button type="button" onClick={chat.clearPendingFile} disabled={chat.sending} aria-label="첨부 취소"><IcoX /></button>
              </div>
            )}
            <div className="cw-inrow">
              <input
                type="file" hidden ref={chat.fileRef}
                accept={chat.attachmentRules?.extensions?.join(',')}
                onChange={chat.pickFile}
              />
              <button type="button" className="cw-attach" onClick={() => chat.fileRef.current?.click()} disabled={chat.sending} aria-label="파일 첨부"><IcoClip /></button>
              <textarea
                className="cw-input" rows={1} placeholder="메시지 입력" aria-label="메시지 입력"
                value={chat.input}
                onChange={(e) => chat.setInput(e.target.value)}
                onKeyDown={chat.onInputKeyDown}
              />
              <button type="button" className="cw-send" onClick={chat.send} disabled={chat.sending}>{chat.sendLabel}</button>
            </div>
            {chat.attachmentRules?.hint && <div className="cw-hint">{chat.attachmentRules.hint}</div>}
          </div>
        </div>
      )}
      <button
        type="button"
        className={`cw-fab${chat.open ? ' cw-on' : ''}`}
        onClick={chat.togglePanel}
        aria-label="상담 채팅"
        aria-expanded={chat.open}
      >
        <IcoChat />
        {chat.unread > 0 && <span className="cw-badge">{chat.unread > 99 ? '99+' : chat.unread}</span>}
      </button>
    </div>
  );
};

export default ChatWidget;
